import {
	type CurrencyEnum,
	type PreviewOrderQuoteOfferFeasibilityInput,
	OrderShippingMethod,
} from '@codezo/graphql';
import { Box, ChevronDown, Clock, Copy } from 'lucide-react';
import { useState } from 'react';
import { Button } from '../../../ui/button';
import { CurrencyInput } from '../../../ui/currency-input';
import { DialogFooter } from '../../../ui/dialog';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '../../../ui/dropdown-menu';
import { FeasibilityFeedback } from '../../../ui/feasibility-badge';
import { NumberInput } from '../../../ui/number-input';
import { ProductImage } from '../../../ui/product-image';
import { cn, formatArrivalWindow } from '../../../../lib/utils';
import { SHIPPING_METHOD_LABEL } from '../../../../lib/status-configs';
import { panelMoney } from '../../../pricing/panel-format';
import {
	type FeasibilityDelivery,
	useFeasibilityPreview,
} from '../../../../hooks/useFeasibilityPreview';
import { useCurrencyStore } from '../../../../stores/currency';
import {
	deliveryIncompleteReason,
	isDeliveryDaysUnknown,
	isDeliveryIncomplete,
	isDeliveryOffered,
} from '../delivery-rules';
import type { DeliveryValues, PricingValues, QuoteItem } from './types';
import { emptyPricing, primaryImageObject } from './types';

/* ── Props ── */

interface WizardDeliveryStepProps {
	quoteItems: QuoteItem[];
	selectedItems: Set<string>;
	customQuantities: Record<string, Array<{ id: string; quantity: number }>>;
	pricing: Record<string, Record<string, PricingValues>>;
	setPricing: React.Dispatch<React.SetStateAction<Record<string, Record<string, PricingValues>>>>;
	/** Quote-level deadline; per-item deadline takes precedence for feasibility. */
	quoteDeadline?: string | null;
	/** Offer accept-by date (worst-case anchor) from step 1, as an ISO string. */
	acceptDeadline?: string | null;
	onBack: () => void;
	onSave: () => void;
	saving: boolean;
	isAddMode: boolean;
}

/** Both methods are always shown; the admin fills in whichever apply. */
const METHODS = [OrderShippingMethod.Standard, OrderShippingMethod.Express];

interface Product {
	item: QuoteItem;
	tiers: Array<{ id: string; quantity: number }>;
}

/* ── Component ── */

/**
 * Shipping for the offer being created, as its own step.
 *
 * It sits after pricing because a delivery is stored against a pricing entry, so
 * the two are saved together — but it does not require a price. A quantity that
 * carries only shipping is sent with a base price of 0. Splitting it out lets
 * every product's arrival be set in one pass rather than buried per tier.
 */
export function WizardDeliveryStep({
	quoteItems,
	selectedItems,
	customQuantities,
	pricing,
	setPricing,
	quoteDeadline,
	acceptDeadline,
	onBack,
	onSave,
	saving,
	isAddMode,
}: WizardDeliveryStepProps) {
	const { selectedCurrency: currency } = useCurrencyStore();

	const allQuantities = (itemId: string) => {
		const item = quoteItems.find((i) => i.id === itemId);
		return [...(item?.quantities ?? []), ...(customQuantities[itemId] ?? [])];
	};

	// Every quantity the client asked for, priced or not — shipping stands on its
	// own here.
	const products = Array.from(selectedItems)
		.map((itemId) => {
			const item = quoteItems.find((i) => i.id === itemId);
			return item ? { item, tiers: allQuantities(itemId) } : null;
		})
		.filter((p): p is Product => !!p);

	const [activeItemId, setActiveItemId] = useState<string | null>(products[0]?.item.id ?? null);
	const [activeQuantityId, setActiveQuantityId] = useState<string | null>(
		products[0]?.tiers[0]?.id ?? null
	);

	const activeProduct = products.find((p) => p.item.id === activeItemId) ?? products[0] ?? null;
	const activeTier =
		activeProduct?.tiers.find((t) => t.id === activeQuantityId) ??
		activeProduct?.tiers[0] ??
		null;
	const activeVals =
		activeProduct && activeTier ? pricing[activeProduct.item.id]?.[activeTier.id] : undefined;

	/** Create or update a method's entry (methods aren't added or removed, just filled in). */
	const upsertDelivery = (method: OrderShippingMethod, patch: Partial<DeliveryValues>) => {
		if (!activeProduct || !activeTier) return;
		// A quantity that was never priced has no entry yet — start one, so
		// shipping can be set on it without a price.
		const vals = activeVals ?? emptyPricing;
		const exists = vals.deliveries.some((d) => d.shippingMethod === method);
		const next = exists
			? vals.deliveries.map((d) => (d.shippingMethod === method ? { ...d, ...patch } : d))
			: [
					...vals.deliveries,
					{
						shippingMethod: method,
						price: '',
						deliveryDaysMin: '',
						deliveryDaysMax: '',
						...patch,
					},
				];
		setPricing((prev) => ({
			...prev,
			[activeProduct.item.id]: {
				...prev[activeProduct.item.id],
				[activeTier.id]: { ...vals, deliveries: next },
			},
		}));
	};

	/** Any field touched on any method — enough to be worth copying across. */
	const hasDeliveryValues = (quantityId: string) =>
		(activeProduct
			? (pricing[activeProduct.item.id]?.[quantityId]?.deliveries ?? [])
			: []
		).some((d) => d.price !== '' || d.deliveryDaysMin !== '' || d.deliveryDaysMax !== '');

	const otherTiers = (activeProduct?.tiers ?? []).filter(
		(t) => t.id !== activeTier?.id && hasDeliveryValues(t.id)
	);

	/** Methods started but not writable, anywhere in the step. A half-filled one
	 * can sit on an off-screen product, so Create says how many and the rail and
	 * tabs mark where. */
	const incompleteTiers = products.flatMap((product) =>
		product.tiers
			.filter((t) =>
				(pricing[product.item.id]?.[t.id]?.deliveries ?? []).some(isDeliveryIncomplete)
			)
			.map((t) => ({ itemId: product.item.id, tierId: t.id }))
	);
	const hasIncomplete = incompleteTiers.length > 0;
	const productHasIncomplete = (itemId: string) =>
		incompleteTiers.some((t) => t.itemId === itemId);
	const tierHasIncomplete = (itemId: string, tierId: string) =>
		incompleteTiers.some((t) => t.itemId === itemId && t.tierId === tierId);

	/** Copy every method from another quantity of the same product. Only the
	 * deliveries move — the target keeps its own price, margin and production days. */
	const copyFromTier = (sourceQuantityId: string) => {
		if (!activeProduct || !activeTier) return;
		const source = pricing[activeProduct.item.id]?.[sourceQuantityId];
		if (!source) return;
		setPricing((prev) => ({
			...prev,
			[activeProduct.item.id]: {
				...prev[activeProduct.item.id],
				[activeTier.id]: {
					...(prev[activeProduct.item.id]?.[activeTier.id] ?? emptyPricing),
					deliveries: source.deliveries.map((d) => ({ ...d })),
				},
			},
		}));
	};

	/* ── Live feasibility for the tier on screen ── */

	const minProductionDays = activeVals?.minDays ? parseInt(activeVals.minDays, 10) : null;
	const maxProductionDays = activeVals?.maxDays ? parseInt(activeVals.maxDays, 10) : null;
	const productionReady = minProductionDays != null && maxProductionDays != null;

	// Send only deliveries whose days are complete — a half-typed one has no
	// arrival to project yet.
	const completeDeliveries = (activeVals?.deliveries ?? [])
		.filter((d) => d.deliveryDaysMin !== '' && d.deliveryDaysMax !== '')
		.map((d) => ({
			shippingMethod: d.shippingMethod,
			deliveryDaysMin: parseInt(d.deliveryDaysMin, 10),
			deliveryDaysMax: parseInt(d.deliveryDaysMax, 10),
		}));

	const deadline = activeProduct?.item.deadline ?? quoteDeadline ?? null;
	const liveInput: PreviewOrderQuoteOfferFeasibilityInput | null = productionReady
		? {
				minProductionDays,
				maxProductionDays,
				deadline: deadline ?? undefined,
				acceptDeadline: acceptDeadline ?? undefined,
				...(completeDeliveries.length > 0 ? { deliveries: completeDeliveries } : {}),
			}
		: null;

	const { production, byMethod, loading: previewLoading } = useFeasibilityPreview(liveInput);
	const readyWindow = formatArrivalWindow(
		production?.projectedCompletion?.ifAcceptedNow?.earliest,
		production?.projectedCompletion?.ifAcceptedNow?.latest
	);

	return (
		<div
			key="delivery"
			className="flex animate-in flex-col fade-in-0 slide-in-from-right-2 duration-200"
		>
			{/* The floor keeps the rail from collapsing on a one-product offer; it's
			    part of the measured height, so it still animates. */}
			{/* Clamped the same way as the standalone delivery editor — see the note
			    there. The floor is the taller of the two: this is one step of a wizard,
			    so a step that sizes to its own content makes the dialog jump on every
			    Next. */}
			<div className="flex max-h-[65vh] min-h-96 border-t border-line-1">
				{/* Product rail */}
				<div className="scroll-thin flex w-44 shrink-0 flex-col gap-0.5 overflow-y-auto border-r border-line-1 p-2">
					{products.map(({ item, tiers }) => {
						const isActive = item.id === activeProduct?.item.id;
						const img = primaryImageObject(item);
						const set = tiers.filter((t) =>
							(pricing[item.id]?.[t.id]?.deliveries ?? []).some(isDeliveryOffered)
						).length;
						return (
							<button
								key={item.id}
								type="button"
								onClick={() => {
									setActiveItemId(item.id);
									setActiveQuantityId(tiers[0]?.id ?? null);
								}}
								className={cn(
									'flex w-full cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors',
									isActive
										? 'bg-accent-soft ring-1 ring-accent-line'
										: 'hover:bg-surface-hover'
								)}
							>
								{img?.url ? (
									<ProductImage
										image={img}
										alt={item.productName || ''}
										className="h-9 w-9 shrink-0 rounded-[var(--r-2)] border border-line-1"
									/>
								) : (
									<div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[var(--r-2)] border border-line-1 bg-surface-fill">
										<Box size={14} className="text-text-4" />
									</div>
								)}
								<div className="min-w-0 flex-1">
									<div className="truncate text-sm font-medium text-text-1">
										{item.productName || 'Unknown'}
									</div>
									<div
										className={cn(
											'text-2xs',
											productHasIncomplete(item.id)
												? 'text-status-warn'
												: 'text-text-4'
										)}
									>
										{productHasIncomplete(item.id)
											? 'Needs attention'
											: `${set}/${tiers.length} shipped`}
									</div>
								</div>
							</button>
						);
					})}
				</div>

				{/* Active product */}
				<div className="flex min-w-0 flex-1 flex-col">
					{activeProduct && activeTier ? (
						<>
							<div className="flex shrink-0 items-center gap-3 border-b border-line-1 px-4 py-3">
								{primaryImageObject(activeProduct.item)?.url ? (
									<ProductImage
										image={primaryImageObject(activeProduct.item)!}
										alt={activeProduct.item.productName || ''}
										className="h-10 w-10 shrink-0 rounded-lg border border-line-1"
									/>
								) : (
									<div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-line-1 bg-surface-fill">
										<Box size={16} className="text-text-4" />
									</div>
								)}
								<div className="min-w-0 flex-1">
									<div className="text-base font-medium text-text-1">
										{activeProduct.item.productName || 'Unknown'}
									</div>
									{activeProduct.item.productSku && (
										<div className="font-mono text-xs text-text-4">
											{activeProduct.item.productSku}
										</div>
									)}
								</div>
							</div>

							{/* Quantity tabs */}
							<div className="flex shrink-0 items-center border-b border-line-1 px-4">
								{activeProduct.tiers.map((q) => {
									const isActiveQ = q.id === activeTier.id;
									const hasDelivery = (
										pricing[activeProduct.item.id]?.[q.id]?.deliveries ?? []
									).some(isDeliveryOffered);
									return (
										<button
											key={q.id}
											type="button"
											onClick={() => setActiveQuantityId(q.id)}
											className={cn(
												'relative px-3 py-2 text-sm font-medium transition-colors',
												isActiveQ
													? 'text-text-1'
													: 'text-text-4 hover:text-text-2'
											)}
										>
											{q.quantity.toLocaleString()} pcs
											{tierHasIncomplete(activeProduct.item.id, q.id) ? (
												<span className="ml-1 inline-block h-1.5 w-1.5 rounded-full bg-status-warn" />
											) : hasDelivery ? (
												<span className="ml-1 inline-block h-1.5 w-1.5 rounded-full bg-accent" />
											) : null}
											{isActiveQ && (
												<div className="absolute right-3 bottom-0 left-3 h-[1.5px] bg-text-1" />
											)}
										</button>
									);
								})}
							</div>

							<div className="p-4">
								<div className="flex flex-col gap-3">
									{otherTiers.length > 0 && (
										<div className="flex items-center justify-end">
											<DropdownMenu>
												<DropdownMenuTrigger className="flex items-center gap-1.5 rounded-md bg-surface-input px-2.5 py-1 text-xs text-text-2 transition-colors hover:text-text-1">
													<Copy size={11} />
													Copy from
													<ChevronDown size={11} />
												</DropdownMenuTrigger>
												<DropdownMenuContent align="end">
													{otherTiers.map((t) => (
														<DropdownMenuItem
															key={t.id}
															onClick={() => copyFromTier(t.id)}
														>
															{t.quantity.toLocaleString()} pcs
														</DropdownMenuItem>
													))}
												</DropdownMenuContent>
											</DropdownMenu>
										</div>
									)}
									<div className="flex items-center justify-between">
										<span className="text-sm font-medium text-text-2">
											Deliveries
										</span>
										<span className="text-2xs text-text-4">
											Leave blank to skip a method
										</span>
									</div>
									<div className="text-2xs text-text-4">
										{activeTier.quantity.toLocaleString()} units
										{readyWindow
											? ` · ready ${readyWindow}`
											: ' · production time not set'}
									</div>
									{METHODS.map((method) => (
										<DeliveryForm
											key={method}
											method={method}
											del={
												activeVals?.deliveries.find(
													(d) => d.shippingMethod === method
												) ?? null
											}
											currency={currency}
											supplierCost={
												activeVals?.vetterQuote?.shipping[method] != null
													? {
															amount: activeVals.vetterQuote.shipping[
																method
															]!,
															currency:
																activeVals.vetterQuote.currency,
															vetterName:
																activeVals.vetterQuote.vetterName,
															prefilled:
																activeVals.vetterQuote.prefilled[
																	method
																],
														}
													: null
											}
											onChange={(patch) => upsertDelivery(method, patch)}
											productionReady={productionReady}
											deadline={deadline}
											result={byMethod.get(method) ?? null}
											previewLoading={previewLoading}
										/>
									))}
								</div>
							</div>
						</>
					) : (
						<div className="flex h-full items-center justify-center p-6 text-center text-sm text-text-4">
							Select a product
						</div>
					)}
				</div>
			</div>

			<DialogFooter>
				{hasIncomplete && (
					<span className="mr-auto text-2xs text-status-warn">
						{incompleteTiers.length === 1
							? '1 quantity has a shipping method that needs a price or both day bounds.'
							: `${incompleteTiers.length} quantities have a shipping method that needs a price or both day bounds.`}
					</span>
				)}
				<Button variant="outline" onClick={onBack}>
					Back
				</Button>
				<Button onClick={onSave} disabled={saving || hasIncomplete}>
					{saving
						? isAddMode
							? 'Adding…'
							: 'Creating…'
						: isAddMode
							? 'Add products'
							: 'Create offer'}
				</Button>
			</DialogFooter>
		</div>
	);
}

/* ── One method on one quantity ── */

function DeliveryForm({
	method,
	del,
	currency,
	supplierCost,
	onChange,
	productionReady,
	deadline,
	result,
	previewLoading,
}: {
	method: OrderShippingMethod;
	del: DeliveryValues | null;
	currency: CurrencyEnum;
	/** The supplier's shipping cost for this method, from their price request. */
	/** `prefilled` is the amount written into the price field, in the form's currency. */
	supplierCost?: {
		amount: number;
		currency: string;
		vetterName: string;
		prefilled?: number;
	} | null;
	onChange: (patch: Partial<DeliveryValues>) => void;
	productionReady: boolean;
	deadline?: string | null;
	result?: FeasibilityDelivery | null;
	previewLoading: boolean;
}) {
	const price = del?.price ?? '';
	const deliveryDaysMin = del?.deliveryDaysMin ?? '';
	const deliveryDaysMax = del?.deliveryDaysMax ?? '';
	const daysComplete = deliveryDaysMin !== '' && deliveryDaysMax !== '';

	return (
		<div className="rounded-lg border border-line-1 p-3">
			<div className="mb-2">
				<span className="text-xs font-medium text-text-1">
					{SHIPPING_METHOD_LABEL[method]}
				</span>
			</div>
			{/* One row: what the method costs, then the lead time it ships on. The
			    day fields are sized to the two or three digits they hold rather than
			    to a grid column — they were only ever as wide as the price because
			    the row had a fourth cell, the admin fee, to balance against. */}
			<div className="flex flex-wrap items-end gap-2">
				<div className="flex min-w-40 max-w-56 flex-1 flex-col gap-1">
					<span className="text-2xs text-text-4">Shipping price</span>
					<CurrencyInput
						currency={currency}
						value={price}
						onChange={(e) => onChange({ price: (e.target as HTMLInputElement).value })}
						placeholder="0.00"
						min={0}
						step="any"
					/>
				</div>
				<div className="flex w-24 flex-col gap-1">
					<span className="text-2xs text-text-4">Min days</span>
					<NumberInput
						value={deliveryDaysMin}
						onChange={(e) =>
							onChange({ deliveryDaysMin: (e.target as HTMLInputElement).value })
						}
						placeholder="—"
						min={0}
					/>
				</div>
				<div className="flex w-24 flex-col gap-1">
					<span className="text-2xs text-text-4">Max days</span>
					<NumberInput
						value={deliveryDaysMax}
						onChange={(e) =>
							onChange({ deliveryDaysMax: (e.target as HTMLInputElement).value })
						}
						placeholder="—"
						min={0}
					/>
				</div>
			</div>

			{/* The supplier's figure is what 05 pays; this field is what the client
			    pays. Prefilled from the one, it must be looked at before sending. */}
			{supplierCost && (
				<div
					className={cn(
						'mt-2 text-2xs',
						price !== '' &&
							Number(price) === (supplierCost.prefilled ?? supplierCost.amount)
							? 'text-status-warn'
							: 'text-text-4'
					)}
				>
					{supplierCost.vetterName}&apos;s shipping cost:{' '}
					{/* In the field's currency, as it was prefilled, with what they
					    really quoted beside it when a rate was applied. */}
					{supplierCost.prefilled != null && supplierCost.currency !== currency
						? `${panelMoney(supplierCost.prefilled, currency)} (quoted ${panelMoney(
								supplierCost.amount,
								supplierCost.currency
							)})`
						: panelMoney(supplierCost.amount, supplierCost.currency)}
					{price !== '' &&
						Number(price) === (supplierCost.prefilled ?? supplierCost.amount) &&
						' — this is their price, not the client’s. Adjust before sending.'}
				</div>
			)}

			{del && isDeliveryIncomplete(del) && (
				<div className="mt-2 text-2xs text-status-warn">
					{deliveryIncompleteReason(del)}
				</div>
			)}

			{isDeliveryDaysUnknown(del ?? undefined) && (
				<div className="mt-2 text-2xs text-text-4">
					Saves without a lead time — add the days before this can be sent to the client.
				</div>
			)}

			{daysComplete && (
				<div className="mt-2">
					{!productionReady ? (
						<span className="inline-flex items-center gap-1 rounded-md bg-surface-fill px-1.5 py-0.5 text-xs font-medium text-text-4">
							<Clock size={11} /> Set production days to preview arrival
						</span>
					) : result ? (
						<FeasibilityFeedback
							verdict={result.verdict}
							projected={result.projectedDelivery}
							deadline={deadline}
							breakdown={result.breakdown}
						/>
					) : previewLoading ? (
						<span className="text-xs text-text-4">Checking arrival…</span>
					) : null}
				</div>
			)}
		</div>
	);
}
